/**
 * Super Admin Dashboard - God Mode
 * Platform owner control panel with tenant management, monitoring, and billing
 */

import React, { useState, useEffect } from 'react';
import {
  Users,
  Eye,
  Power,
  AlertTriangle,
  TrendingUp,
  DollarSign,
  Server,
  Cpu,
  HardDrive,
  Key,
  Calendar,
  Plus,
  X,
  Search,
  Filter,
  MoreVertical,
  RefreshCw,
  Download,
  Zap,
  Activity,
  CheckCircle,
  XCircle,
  Clock,
  BarChart3,
  Wallet,
  Target,
  Edit,
  Save,
  Settings,
  Globe,
  Bot,
  Shield,
  Crown,
  Sparkles,
} from 'lucide-react';

import HunterMonitor from './HunterMonitor';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

const SuperAdminDashboard = ({ user, onLogout, onImpersonate }) => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tenants');
  const [showCreateTenant, setShowCreateTenant] = useState(false);
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [showEditTenant, setShowEditTenant] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', email: '', company_name: '', new_password: '', subscription_plan: 'free', subscription_status: 'trial', billing_cycle: 'monthly' });
  const [editLoading, setEditLoading] = useState(false);
  const [editMsg, setEditMsg] = useState(null);
  const [newTenant, setNewTenant] = useState({ email: '', name: '', company_name: '', password: '' });
  const [searchQuery, setSearchQuery] = useState('');
  // Hunter robot settings
  const [hunterSettings, setHunterSettings] = useState({
    proxy_list: '',
    rotation_interval: 30,
    max_concurrent: 3,
    retry_attempts: 2,
    request_delay_ms: 1500,
    user_agent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
    headless: true,
    enable_captcha_solver: false,
    captcha_api_key: '',
  });
  const [hunterSaving, setHunterSaving] = useState(false);
  const [hunterSaveMsg, setHunterSaveMsg] = useState(null);

  // Monitoring State
  const [tokenUsage, setTokenUsage] = useState([]);
  const [systemErrors, setSystemErrors] = useState([]);
  const [serverStatus, setServerStatus] = useState({ cpu: 0, ram: 0, containers: [] });

  // Billing State
  const [mrrData, setMrrData] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [licenseKeys, setLicenseKeys] = useState([]);

  const token = localStorage.getItem('token');

  // Fetch all data
  useEffect(() => {
    fetchTenants();
    fetchMonitoringData();
    fetchBillingData();
  }, []);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTenants(Array.isArray(data) ? data : (data.tenants || []));
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      // Sample data for demo
      setTenants([
        { id: 1, name: 'Palm Realty Dubai', email: 'admin@palmrealty.ae', subscription_status: 'active', created_at: '2024-01-15', leads_count: 245 },
        { id: 2, name: 'Marina Properties', email: 'info@marinaprop.com', subscription_status: 'active', created_at: '2024-02-20', leads_count: 189 },
        { id: 3, name: 'Downtown Estates', email: 'contact@downtown.ae', subscription_status: 'trial', created_at: '2024-03-10', leads_count: 56 },
        { id: 4, name: 'JBR Investments', email: 'hello@jbrinvest.com', subscription_status: 'suspended', created_at: '2024-01-05', leads_count: 312 },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonitoringData = async () => {
    try {
      // Fetch token usage — API returns {period, total_tokens, total_cost, daily_data: [...]}
      const tokenRes = await fetch(`${API_BASE_URL}/api/admin/monitoring/tokens`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (tokenRes.ok) {
        const data = await tokenRes.json();
        // Normalize: API returns {daily_data:[{date,tokens,cost,api_calls}]}
        // UI expects [{tenant, tokens, cost}]
        if (Array.isArray(data)) {
          setTokenUsage(data);
        } else if (data?.daily_data && Array.isArray(data.daily_data)) {
          setTokenUsage(data.daily_data.map(d => ({
            tenant: d.date || 'Unknown',
            tokens: d.tokens || 0,
            cost: d.cost || 0,
          })));
        } else {
          // Show summary as single item
          setTokenUsage([{
            tenant: `Total (${data?.period || '30d'})`,
            tokens: data?.total_tokens || 0,
            cost: data?.total_cost || 0,
          }]);
        }
      }

      // Fetch system errors — API returns {errors: [...], total: N}
      const errorRes = await fetch(`${API_BASE_URL}/api/admin/monitoring/errors`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (errorRes.ok) {
        const data = await errorRes.json();
        // Normalize: API returns {errors: [{id,type,message,source,severity,tenant_id,is_resolved,created_at}]}
        const rawErrors = Array.isArray(data) ? data : (data?.errors || []);
        setSystemErrors(rawErrors.map((e, i) => ({
          id: e.id || i,
          type: e.severity || e.type || 'error',
          message: e.error_message || e.message || 'Unknown error',
          timestamp: e.created_at || e.timestamp || '',
          resolved: e.is_resolved ?? e.resolved ?? false,
        })));
      }

      // Fetch server status — API returns {cpu_percent, memory_percent, disk_percent, status}
      const serverRes = await fetch(`${API_BASE_URL}/api/admin/monitoring/servers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (serverRes.ok) {
        const data = await serverRes.json();
        setServerStatus({
          cpu: data.cpu_percent ?? data.cpu ?? 0,
          ram: data.memory_percent ?? data.ram ?? 0,
          containers: data.containers || [
            { name: 'backend', status: data.status === 'healthy' ? 'running' : 'stopped', ram: 0 },
            { name: 'postgres', status: 'running', ram: 0 },
            { name: 'redis', status: 'running', ram: 0 },
            { name: 'nginx', status: 'running', ram: 0 },
          ]
        });
      }
    } catch (error) {
      console.error('Error fetching monitoring data:', error);
      // Fallback data
      setTokenUsage([]);
      setSystemErrors([]);
      setServerStatus({ cpu: 0, ram: 0, containers: [] });
    }
  };

  const fetchBillingData = async () => {
    try {
      // Fetch MRR
      const mrrRes = await fetch(`${API_BASE_URL}/api/admin/billing/mrr`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (mrrRes.ok) {
        const data = await mrrRes.json();
        setMrrData(Array.isArray(data) ? data : (data?.monthly_data || []));
      }

      // Fetch invoices
      const invRes = await fetch(`${API_BASE_URL}/api/admin/billing/invoices`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (invRes.ok) {
        const data = await invRes.json();
        setInvoices(Array.isArray(data) ? data : (data?.invoices || []));
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      // Sample data for demo
      setMrrData([
        { month: 'Jul', revenue: 8500 },
        { month: 'Aug', revenue: 12000 },
        { month: 'Sep', revenue: 15500 },
        { month: 'Oct', revenue: 18200 },
        { month: 'Nov', revenue: 22000 },
        { month: 'Dec', revenue: 25500 },
      ]);
      setInvoices([
        { id: 'INV-001', tenant: 'Palm Realty Dubai', amount: 499, status: 'paid', date: '2024-12-01' },
        { id: 'INV-002', tenant: 'Marina Properties', amount: 299, status: 'paid', date: '2024-12-01' },
        { id: 'INV-003', tenant: 'Downtown Estates', amount: 0, status: 'trial', date: '2024-12-10' },
        { id: 'INV-004', tenant: 'JBR Investments', amount: 499, status: 'overdue', date: '2024-11-01' },
      ]);
    }
  };

  // Tenant Actions
  const handleImpersonate = (tenant) => {
    sessionStorage.setItem('admin_fallback_token', token);
    sessionStorage.setItem('impersonating_tenant', JSON.stringify(tenant));
    onImpersonate(tenant);
  };

  const handleSuspendTenant = async (tenantId) => {
    if (!confirm('Are you sure you want to suspend this tenant? They will lose access immediately.')) return;

    try {
      await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/suspend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTenants();
    } catch (error) {
      console.error('Error suspending tenant:', error);
      // Update UI optimistically
      setTenants(tenants.map(t =>
        t.id === tenantId ? { ...t, subscription_status: 'suspended' } : t
      ));
    }
  };

  const handleActivateTenant = async (tenantId) => {
    try {
      await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      fetchTenants();
    } catch (error) {
      console.error('Error activating tenant:', error);
      setTenants(tenants.map(t =>
        t.id === tenantId ? { ...t, subscription_status: 'active' } : t
      ));
    }
  };

  const handleChangePlan = async (tenantId, newPlan) => {
    try {
      await fetch(`${API_BASE_URL}/api/admin/tenants/${tenantId}/subscription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newPlan })
      });
      fetchTenants();
    } catch (error) {
      console.error('Error changing plan:', error);
    }
  };

  const handleGenerateLicense = async (tenantId, plan) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/billing/license`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ tenant_id: tenantId, plan })
      });
      if (response.ok) {
        const data = await response.json();
        alert(`License Key Generated: ${data.license_key}`);
        setLicenseKeys([...licenseKeys, data]);
      }
    } catch (error) {
      console.error('Error generating license:', error);
      // Generate sample key for demo
      const key = `LIC-${Date.now().toString(36).toUpperCase()}-${Math.random().toString(36).substr(2, 8).toUpperCase()}`;
      alert(`License Key Generated: ${key}`);
    }
  };

  // ── Edit Tenant ────────────────────────────────────────────────────────────
  const openEditTenant = (tenant) => {
    setEditingTenant(tenant);
    setEditForm({
      name: tenant.name || '',
      email: tenant.email || '',
      company_name: tenant.company_name || '',
      new_password: '',
      subscription_plan: tenant.subscription_plan || 'free',
      subscription_status: tenant.subscription_status || 'trial',
      billing_cycle: tenant.billing_cycle || 'monthly',
    });
    setEditMsg(null);
    setShowEditTenant(true);
  };

  const handleEditTenant = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditMsg(null);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        company_name: editForm.company_name,
        subscription_plan: editForm.subscription_plan,
        subscription_status: editForm.subscription_status,
        billing_cycle: editForm.billing_cycle,
      };
      if (editForm.new_password.trim()) payload.password = editForm.new_password;
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants/${editingTenant.id}/credentials`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        setEditMsg({ type: 'success', text: 'Tenant updated successfully!' });
        fetchTenants();
        setTimeout(() => setShowEditTenant(false), 1200);
      } else {
        const err = await response.json().catch(() => ({}));
        setEditMsg({ type: 'error', text: err.detail || 'Update failed.' });
      }
    } catch {
      setEditMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setEditLoading(false);
    }
  };

  // ── Hunter Settings ─────────────────────────────────────────────────────────
  const handleSaveHunterSettings = async () => {
    setHunterSaving(true);
    setHunterSaveMsg(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/hunter/admin/settings`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(hunterSettings),
      });
      if (response.ok) {
        setHunterSaveMsg({ type: 'success', text: '✅ Hunter settings saved!' });
      } else {
        setHunterSaveMsg({ type: 'error', text: '⚠️ Failed to save. Settings stored locally.' });
      }
    } catch {
      setHunterSaveMsg({ type: 'error', text: '⚠️ Network error — settings stored locally only.' });
    } finally {
      setHunterSaving(false);
      setTimeout(() => setHunterSaveMsg(null), 3000);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/tenants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newTenant)
      });
      if (response.ok) {
        setShowCreateTenant(false);
        setNewTenant({ email: '', name: '', company_name: '', password: '' });
        fetchTenants();
        alert('Tenant created successfully!');
      } else if (response.status === 401) {
        // Token expired - redirect to login
        const error = await response.json();
        alert('Session expired. Please login again.');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to create tenant'}`);
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      alert('Failed to create tenant. Please try again.');
    }
  };

  // Status Badge Component
  const StatusBadge = ({ status }) => {
    const config = {
      active: { class: 'badge-green', icon: CheckCircle, label: 'Active' },
      trial: { class: 'badge-blue', icon: Clock, label: 'Trial' },
      suspended: { class: 'badge-red', icon: XCircle, label: 'Suspended' },
      cancelled: { class: 'badge-yellow', icon: XCircle, label: 'Cancelled' },
      expired: { class: 'badge-yellow', icon: AlertTriangle, label: 'Expired' },
      overdue: { class: 'badge-yellow', icon: AlertTriangle, label: 'Overdue' },
      paid: { class: 'badge-green', icon: CheckCircle, label: 'Paid' },
    };
    const { class: badgeClass, icon: Icon, label } = config[status] || config.active;

    return (
      <span className={`badge ${badgeClass} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </span>
    );
  };

  // Plan Badge Component
  const PlanBadge = ({ plan }) => {
    const config = {
      free: { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', label: 'Free', icon: null },
      basic: { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30', label: 'Basic', icon: Sparkles },
      pro: { bg: 'bg-gold-500/20', text: 'text-gold-400', border: 'border-gold-500/30', label: 'Pro', icon: Crown },
    };
    const { bg, text, border, label, icon: PIcon } = config[plan] || config.free;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${bg} ${text} border ${border}`}>
        {PIcon && <PIcon className="w-3 h-3" />}
        {label}
      </span>
    );
  };

  // Filter tenants by search
  const filteredTenants = tenants.filter(t =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate total MRR
  const totalMRR = mrrData.length > 0 ? mrrData[mrrData.length - 1].revenue : 0;
  const maxMRR = Math.max(...mrrData.map(d => d.revenue), 1);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800 flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-navy-950 via-navy-900 to-navy-800">
      {/* Header */}
      <header className="glass-header px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Brand logo — goldlogo.svg + two-tone wordmark */}
          <img src="/goldlogo.svg" alt="Artin Logo" style={{ width: 36, height: 36, objectFit: 'contain', flexShrink: 0 }} />
          <div>
            <h1 className="font-bold text-base tracking-tight" style={{ margin: 0 }}>
              <span className="text-white">ArtinSmart</span><span className="text-gold-400">Realty</span>
            </h1>
            <p className="text-xs text-gray-400" style={{ margin: '1px 0 0' }}>Super Admin Panel</p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <button onClick={fetchTenants} className="btn-icon" title="Refresh">
            <RefreshCw className="w-5 h-5" />
          </button>
          <button onClick={onLogout} className="btn-outline">
            Logout
          </button>
        </div>
      </header>

      {/* Navigation Tabs */}
      <div className="px-6 pt-6">
        <div className="flex gap-2 p-1 bg-navy-800/50 rounded-xl inline-flex">
          {[
            { id: 'tenants',    label: 'Tenant Management', icon: Users },
            { id: 'monitoring', label: 'AI & Monitoring',    icon: Activity },
            { id: 'billing',    label: 'Billing & Finance',  icon: DollarSign },
            { id: 'hunter',     label: 'Realty Hunter',      icon: Target },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${activeTab === tab.id
                ? 'bg-gold-500/20 text-gold-400'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* HUNTER TAB */}
        {activeTab === 'hunter' && (
          <div className="space-y-6">
            <HunterMonitor />

            {/* ── Robot Settings ── */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <Bot className="w-5 h-5 text-gold-400" />
                Hunter Robot Settings
              </h3>

              {hunterSaveMsg && (
                <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                  hunterSaveMsg.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>{hunterSaveMsg.text}</div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Proxy Settings */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2 uppercase tracking-wider">
                    <Globe className="w-4 h-4" /> Proxy Configuration
                  </h4>
                  <div>
                    <label className="input-label">Proxy List (one per line: ip:port:user:pass)</label>
                    <textarea
                      rows={5}
                      value={hunterSettings.proxy_list}
                      onChange={e => setHunterSettings({ ...hunterSettings, proxy_list: e.target.value })}
                      className="input-field font-mono text-xs"
                      placeholder="185.162.231.45:8000:user:pass&#10;45.87.246.12:8000:user:pass"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="input-label">Rotation Interval (sec)</label>
                      <input type="number" min={10} max={300}
                        value={hunterSettings.rotation_interval}
                        onChange={e => setHunterSettings({ ...hunterSettings, rotation_interval: +e.target.value })}
                        className="input-field" />
                    </div>
                    <div>
                      <label className="input-label">Max Concurrent Jobs</label>
                      <input type="number" min={1} max={20}
                        value={hunterSettings.max_concurrent}
                        onChange={e => setHunterSettings({ ...hunterSettings, max_concurrent: +e.target.value })}
                        className="input-field" />
                    </div>
                  </div>
                </div>

                {/* Crawler Settings */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2 uppercase tracking-wider">
                    <Settings className="w-4 h-4" /> Crawler Behavior
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="input-label">Request Delay (ms)</label>
                      <input type="number" min={500} max={10000} step={100}
                        value={hunterSettings.request_delay_ms}
                        onChange={e => setHunterSettings({ ...hunterSettings, request_delay_ms: +e.target.value })}
                        className="input-field" />
                    </div>
                    <div>
                      <label className="input-label">Retry Attempts</label>
                      <input type="number" min={0} max={10}
                        value={hunterSettings.retry_attempts}
                        onChange={e => setHunterSettings({ ...hunterSettings, retry_attempts: +e.target.value })}
                        className="input-field" />
                    </div>
                  </div>
                  <div>
                    <label className="input-label">User Agent</label>
                    <input type="text"
                      value={hunterSettings.user_agent}
                      onChange={e => setHunterSettings({ ...hunterSettings, user_agent: e.target.value })}
                      className="input-field text-xs" />
                  </div>
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={hunterSettings.headless}
                        onChange={e => setHunterSettings({ ...hunterSettings, headless: e.target.checked })}
                        className="w-4 h-4 accent-gold-500" />
                      <span className="text-sm text-gray-300">Headless mode</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={hunterSettings.enable_captcha_solver}
                        onChange={e => setHunterSettings({ ...hunterSettings, enable_captcha_solver: e.target.checked })}
                        className="w-4 h-4 accent-gold-500" />
                      <span className="text-sm text-gray-300">Captcha Solver</span>
                    </label>
                  </div>
                  {hunterSettings.enable_captcha_solver && (
                    <div>
                      <label className="input-label">2Captcha / Anti-Captcha API Key</label>
                      <input type="text"
                        value={hunterSettings.captcha_api_key}
                        onChange={e => setHunterSettings({ ...hunterSettings, captcha_api_key: e.target.value })}
                        className="input-field" placeholder="Enter API key..." />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleSaveHunterSettings}
                  disabled={hunterSaving}
                  className="btn-gold flex items-center gap-2"
                >
                  {hunterSaving ? <div className="w-4 h-4 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Hunter Settings
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TENANT MANAGEMENT TAB */}
        {activeTab === 'tenants' && (
          <div className="space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon"><Users className="w-6 h-6 text-gold-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{tenants.length}</p>
                    <p className="text-sm text-gray-400">Total Tenants</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon bg-green-500/20"><CheckCircle className="w-6 h-6 text-green-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{tenants.filter(t => t.subscription_status === 'active').length}</p>
                    <p className="text-sm text-gray-400">Active</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon bg-blue-500/20"><Clock className="w-6 h-6 text-blue-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{tenants.filter(t => t.subscription_status === 'trial').length}</p>
                    <p className="text-sm text-gray-400">On Trial</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon bg-red-500/20"><XCircle className="w-6 h-6 text-red-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">{tenants.filter(t => t.subscription_status === 'suspended').length}</p>
                    <p className="text-sm text-gray-400">Suspended</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Actions */}
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tenants..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input w-full pl-12"
                />
              </div>
              <button
                onClick={() => setShowCreateTenant(true)}
                className="btn-gold flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Tenant
              </button>
            </div>

            {/* Tenant Table */}
            <div className="glass-card overflow-hidden">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Tenant</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Status</th>
                    <th>Leads</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTenants.map((tenant) => (
                    <tr key={tenant.id}>
                      <td className="font-medium text-white">{tenant.name}</td>
                      <td>{tenant.email}</td>
                      <td><PlanBadge plan={tenant.subscription_plan} /></td>
                      <td><StatusBadge status={tenant.subscription_status} /></td>
                      <td>{tenant.leads_count || tenant.total_leads || 0}</td>
                      <td>{new Date(tenant.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleImpersonate(tenant)}
                            className="p-2 rounded-lg hover:bg-navy-700 text-blue-400 transition-all"
                            title="Impersonate"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {tenant.subscription_status === 'suspended' ? (
                            <button
                              onClick={() => handleActivateTenant(tenant.id)}
                              className="p-2 rounded-lg hover:bg-green-500/20 text-green-400 transition-all"
                              title="Activate"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleSuspendTenant(tenant.id)}
                              className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-all"
                              title="Kill Switch"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => openEditTenant(tenant)}
                            className="p-2 rounded-lg hover:bg-blue-500/20 text-blue-400 transition-all"
                            title="Edit Tenant"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleGenerateLicense(tenant.id, 'lifetime')}
                            className="p-2 rounded-lg hover:bg-gold-500/20 text-gold-400 transition-all"
                            title="Generate License"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* MONITORING TAB */}
        {activeTab === 'monitoring' && (
          <div className="space-y-6">
            {/* Token Usage */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-gold-400" />
                AI Token Usage (This Month)
              </h3>
              <div className="space-y-4">
                {tokenUsage.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-white">{item.tenant || 'Unknown'}</span>
                        <span className="text-sm text-gray-400">{(item.tokens || 0).toLocaleString()} tokens</span>
                      </div>
                      <div className="h-2 bg-navy-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-gold-400 to-gold-600 rounded-full"
                          style={{ width: `${Math.min(((item.tokens || 0) / 50000) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm text-gold-400 font-medium w-16 text-right">${item.cost || 0}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Server Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Server className="w-5 h-5 text-gold-400" />
                  Server Status
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-navy-800/50 rounded-xl p-4 text-center">
                    <Cpu className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{serverStatus.cpu}%</p>
                    <p className="text-xs text-gray-400">CPU Usage</p>
                  </div>
                  <div className="bg-navy-800/50 rounded-xl p-4 text-center">
                    <HardDrive className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-white">{serverStatus.ram}%</p>
                    <p className="text-xs text-gray-400">RAM Usage</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {serverStatus.containers?.map((container, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-navy-800/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${container.status === 'running' ? 'bg-green-400' : 'bg-red-400'}`} />
                        <span className="text-sm text-white">{container.name}</span>
                      </div>
                      <span className="text-xs text-gray-400">{container.ram}MB</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error Monitoring */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-400" />
                  System Errors
                </h3>
                <div className="space-y-3 max-h-[300px] overflow-y-auto">
                  {systemErrors.map((error) => (
                    <div
                      key={error.id}
                      className={`p-3 rounded-lg border-l-4 ${error.type === 'error' ? 'border-l-red-500 bg-red-500/5' :
                        error.type === 'warning' ? 'border-l-yellow-500 bg-yellow-500/5' :
                          'border-l-blue-500 bg-blue-500/5'
                        }`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm text-white">{error.message}</p>
                          <p className="text-xs text-gray-500 mt-1">{error.timestamp}</p>
                        </div>
                        <span className={`badge ${error.resolved ? 'badge-green' : 'badge-yellow'}`}>
                          {error.resolved ? 'Resolved' : 'Active'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* BILLING TAB */}
        {activeTab === 'billing' && (
          <div className="space-y-6">
            {/* MRR Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon"><DollarSign className="w-6 h-6 text-gold-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">${totalMRR.toLocaleString()}</p>
                    <p className="text-sm text-gray-400">Monthly Revenue (MRR)</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon bg-green-500/20"><TrendingUp className="w-6 h-6 text-green-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">+16%</p>
                    <p className="text-sm text-gray-400">Growth Rate</p>
                  </div>
                </div>
              </div>
              <div className="glass-card p-5">
                <div className="flex items-center gap-4">
                  <div className="kpi-icon"><Wallet className="w-6 h-6 text-gold-400" /></div>
                  <div>
                    <p className="text-2xl font-bold text-white">${(totalMRR * 12).toLocaleString()}</p>
                    <p className="text-sm text-gray-400">ARR (Annual)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* MRR Chart */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-semibold text-white mb-6 flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-gold-400" />
                Revenue Trend
              </h3>
              <div className="flex items-end gap-4 h-48">
                {mrrData.map((item, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-2">
                    <div
                      className="w-full bg-gradient-to-t from-gold-500 to-gold-400 rounded-t-lg transition-all hover:from-gold-400 hover:to-gold-300"
                      style={{ height: `${(item.revenue / maxMRR) * 100}%` }}
                    />
                    <span className="text-xs text-gray-400">{item.month}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Invoices Table */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  Invoices
                </h3>
                <button className="btn-outline flex items-center gap-2 text-sm">
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Tenant</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="font-mono text-gold-400">{invoice.id}</td>
                      <td>{invoice.tenant}</td>
                      <td className="font-medium text-white">${invoice.amount}</td>
                      <td><StatusBadge status={invoice.status} /></td>
                      <td>{new Date(invoice.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Create Tenant Modal */}
      {showCreateTenant && (
        <div className="modal-overlay" onClick={() => setShowCreateTenant(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Add New Tenant</h3>
              <button onClick={() => setShowCreateTenant(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateTenant} className="space-y-4">
              <div>
                <label className="input-label">Name</label>
                <input
                  type="text"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="input-label">Email</label>
                <input
                  type="email"
                  value={newTenant.email}
                  onChange={(e) => setNewTenant({ ...newTenant, email: e.target.value })}
                  className="input-field"
                  required
                />
              </div>
              <div>
                <label className="input-label">Company Name</label>
                <input
                  type="text"
                  value={newTenant.company_name}
                  onChange={(e) => setNewTenant({ ...newTenant, company_name: e.target.value })}
                  className="input-field"
                />
              </div>
              <div>
                <label className="input-label">Password</label>
                <input
                  type="password"
                  value={newTenant.password}
                  onChange={(e) => setNewTenant({ ...newTenant, password: e.target.value })}
                  className="input-field"
                  required
                  minLength={8}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowCreateTenant(false)} className="flex-1 btn-ghost">
                  Cancel
                </button>
                <button type="submit" className="flex-1 btn-gold">
                  Create Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Tenant Modal */}
      {showEditTenant && editingTenant && (
        <div className="modal-overlay" onClick={() => setShowEditTenant(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <Edit className="w-5 h-5 text-gold-400" />
                Edit Tenant: {editingTenant.name}
              </h3>
              <button onClick={() => setShowEditTenant(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            {editMsg && (
              <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
                editMsg.type === 'success' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
              }`}>{editMsg.text}</div>
            )}

            <form onSubmit={handleEditTenant} className="space-y-4">
              {/* Account Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Name</label>
                  <input type="text" required
                    value={editForm.name}
                    onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    className="input-field" />
                </div>
                <div>
                  <label className="input-label">Email (Login Username)</label>
                  <input type="email" required
                    value={editForm.email}
                    onChange={e => setEditForm({ ...editForm, email: e.target.value })}
                    className="input-field" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Company Name</label>
                  <input type="text"
                    value={editForm.company_name}
                    onChange={e => setEditForm({ ...editForm, company_name: e.target.value })}
                    className="input-field" />
                </div>
                <div>
                  <label className="input-label">New Password <span className="text-gray-500">(leave empty to keep current)</span></label>
                  <input type="password" minLength={6}
                    value={editForm.new_password}
                    onChange={e => setEditForm({ ...editForm, new_password: e.target.value })}
                    className="input-field"
                    placeholder="••••••••" />
                </div>
              </div>

              {/* Subscription Section */}
              <div className="border-t border-white/10 pt-4 mt-2">
                <h4 className="text-sm font-semibold text-gold-400 flex items-center gap-2 mb-3 uppercase tracking-wider">
                  <Crown className="w-4 h-4" />
                  Subscription Management
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {/* Plan Selector */}
                  <div>
                    <label className="input-label">Plan</label>
                    <select
                      value={editForm.subscription_plan}
                      onChange={e => setEditForm({ ...editForm, subscription_plan: e.target.value })}
                      className="input-field"
                    >
                      <option value="free">🆓 Free</option>
                      <option value="basic">⭐ Basic — $299/mo</option>
                      <option value="pro">👑 Pro — $399/mo</option>
                    </select>
                  </div>
                  {/* Status Selector */}
                  <div>
                    <label className="input-label">Status</label>
                    <select
                      value={editForm.subscription_status}
                      onChange={e => setEditForm({ ...editForm, subscription_status: e.target.value })}
                      className="input-field"
                    >
                      <option value="trial">⏳ Trial</option>
                      <option value="active">✅ Active</option>
                      <option value="suspended">🚫 Suspended</option>
                      <option value="cancelled">❌ Cancelled</option>
                      <option value="expired">⌛ Expired</option>
                    </select>
                  </div>
                  {/* Billing Cycle */}
                  <div>
                    <label className="input-label">Billing Cycle</label>
                    <select
                      value={editForm.billing_cycle}
                      onChange={e => setEditForm({ ...editForm, billing_cycle: e.target.value })}
                      className="input-field"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly (Save 25%)</option>
                    </select>
                  </div>
                </div>

                {/* Plan comparison hint */}
                {editForm.subscription_plan !== 'free' && (
                  <div className="mt-3 p-3 rounded-lg bg-gold-500/5 border border-gold-500/20">
                    <p className="text-xs text-gold-400 font-medium mb-1">
                      {editForm.subscription_plan === 'pro' ? '👑 Pro Plan' : '⭐ Basic Plan'} Features:
                    </p>
                    <p className="text-xs text-gray-400">
                      {editForm.subscription_plan === 'pro'
                        ? 'Bot + Follow-up + Lead Generation + LinkedIn Scraper + All Features'
                        : 'Bot + Follow-up System (No Lead Generation)'}
                    </p>
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowEditTenant(false)} className="flex-1 btn-ghost">Cancel</button>
                <button type="submit" disabled={editLoading} className="flex-1 btn-gold flex items-center justify-center gap-2">
                  {editLoading ? <div className="w-4 h-4 border-2 border-navy-900 border-t-transparent rounded-full animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
